import { Product } from "@deliverr/commons-clients";
import { InboundPackageSummary, ShippingPlanItemLotExpirationRaw } from "@deliverr/inbound-client";
import { CasePackDefaultData } from "common/clients/inbound/bulkUpload/CasePackDefaultData";
import { useCreateShipmentFormContext } from "inbounds/createShipment/useCreateShipmentFormContext";
import { CreateShipmentInputName } from "inbounds/createShipment/useCreateShipmentForm";
import { batch, useDispatch } from "react-redux";
import { bulkUploadBoxConfigProducts } from "../../../SelectStorageProductsActions";
import { getStorageItemDetails } from "../../../getStorageItemDetails";
import { useFieldArray } from "react-hook-form";
import { useCallback } from "react";

interface FormItem {
  dsku: string;
  qty: number;
}

export const useMixedSkuBulkUpload = (index?: number) => {
  const dispatch = useDispatch();
  const { control, setValue, getValues } = useCreateShipmentFormContext();
  const indexOfPackageConfig = index ?? 0;

  // Package paths with explicit type casting for form field names
  const itemDetailsPath = `${CreateShipmentInputName.MIXED_SKU_PACKAGES}.${indexOfPackageConfig}.items` as const;
  const getPackagePath = useCallback(
    (field: string) => `${CreateShipmentInputName.MIXED_SKU_PACKAGES}.${indexOfPackageConfig}.${field}` as any,
    [indexOfPackageConfig]
  );

  const {
    fields: mixedSkuPackage,
    append: appendMixedSkuPackage,
    remove: removeMixedSkuPackage,
  } = useFieldArray({
    control,
    name: itemDetailsPath,
  });

  const setPackageDimensions = useCallback(
    (dimensions: { numberOfPkgs?: number; weight?: number; length?: number; width?: number; height?: number }) => {
      const { numberOfPkgs = 1, weight = 0, length = 0, width = 0, height = 0 } = dimensions;

      // Batch all package dimension updates to reduce form re-renders
      batch(() => {
        setValue(getPackagePath("numberOfPkgs"), numberOfPkgs);
        setValue(getPackagePath("weight"), weight);
        setValue(getPackagePath("length"), length);
        setValue(getPackagePath("width"), width);
        setValue(getPackagePath("height"), height);
      });
    },
    [setValue, getPackagePath]
  );

  const formatItems = useCallback((products: (Product & { qty: number })[], numberOfPkgs = 1): FormItem[] => {
    return products.map((product) => ({
      dsku: product.dsku,
      // If numberOfPkgs is greater than 1, divide the qty by numberOfPkgs to get units per box
      qty: numberOfPkgs > 1 ? Math.floor(product.qty / numberOfPkgs) : product.qty ?? 0,
    }));
  }, []);

  // Optimized form update with selective verification for large datasets
  const updateFormItems = useCallback(
    (formattedItems: FormItem[]) => {
      // Set entire array at once for optimal performance
      setValue(itemDetailsPath, formattedItems);

      // For large datasets (1000+), skip individual item verification to avoid performance issues
      // The bulk setValue above is sufficient and more performant
      // Only perform verification for smaller datasets
      setTimeout(() => {
        const updatedItems = getValues(itemDetailsPath) as FormItem[] | undefined;
        if (updatedItems && updatedItems.length === formattedItems.length) {
          // Batch individual corrections to minimize re-renders
          const corrections: { path: string; value: number }[] = [];

          formattedItems.forEach((item, idx) => {
            if (idx < updatedItems.length && updatedItems[idx] && updatedItems[idx].qty !== item.qty) {
              corrections.push({
                path: `${itemDetailsPath}.${idx}.qty`,
                value: item.qty,
              });
            }
          });

          // Apply all corrections in a single batch
          if (corrections.length > 0) {
            batch(() => {
              corrections.forEach(({ path, value }) => {
                setValue(path as any, value);
              });
            });
          }
        }
      }, 100);
    },
    [setValue, getValues, itemDetailsPath]
  );

  const resetFormValues = useCallback(() => {
    // Clear items array
    setValue(itemDetailsPath, []);
    // Initialize package fields with defaults
    setPackageDimensions({ numberOfPkgs: 0 });
  }, [setValue, itemDetailsPath, setPackageDimensions]);

  const handleIntlDtcMixedSkuBulkUploadSuccess = useCallback(
    async (
      products: (Product & { qty: number })[],
      caseDimensions?: CasePackDefaultData[],
      lotTracking?: { [dsku: string]: ShippingPlanItemLotExpirationRaw },
      packages?: InboundPackageSummary[]
    ): Promise<void> => {
      if (!products.length) {
        return;
      }

      resetFormValues();

      const packageToUse = packages && packages.length > 0 ? packages[0] : undefined;

      // Store initial product data from CSV (contains MSKU but needs product names from API)
      dispatch(bulkUploadBoxConfigProducts(products, packageToUse, indexOfPackageConfig));

      setPackageDimensions(packageToUse ?? { numberOfPkgs: 1 });

      // The getStorageItemDetails already handles batching via getUnifiedProducts
      // which batches API calls at 200 DSKUs per request with 5 parallel requests
      try {
        await dispatch(getStorageItemDetails(products));

        // Now update form items after product names are loaded
        const numberOfPkgs = packageToUse?.numberOfPkgs ?? 1;
        const formattedItems = formatItems(products, numberOfPkgs);
        updateFormItems(formattedItems);
      } catch (error) {
        // If API fails, still update form with quantities (names might be missing)
        const numberOfPkgs = packageToUse?.numberOfPkgs ?? 1;
        const formattedItems = formatItems(products, numberOfPkgs);
        updateFormItems(formattedItems);
      }
    },
    [resetFormValues, dispatch, indexOfPackageConfig, setPackageDimensions, formatItems, updateFormItems]
  );

  return {
    mixedSkuPackage,
    appendMixedSkuPackage,
    removeMixedSkuPackage,
    handleIntlDtcMixedSkuBulkUploadSuccess,
  };
};
